import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    MediumFacade,
    MediumType,
    ProductsFacade,
    PurchaseFacade,
    RelatedCardFacade,
    ShoppingCartFacade
} from '@ridango/components';
import Env from '@ridango/core/env';

import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// import { combineLatest } from 'rxjs-compat/operator/combineLatest';

@Component({
    selector: 'gl-wallet-loading-page-v1',
    templateUrl: './wallet-loading-page.component.html',
    styleUrls: ['./wallet-loading-page.component.scss']
})
export class WalletLoadingPageComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();

    allIds$ = this.productsFacade.allIds$;
    loading$ = this.productsFacade.loading$;
    fixedSumProducts$ = this.productsFacade.fixedSumProducts$;
    variableSumProduct$ = this.productsFacade.variableSumProduct$;

    // steps = ['TICKET_TEXT_ADD_DATA', 'TICKET_TEXT_PAYMENT', 'TICKET_TEXT_TICKETS'];
    steps$: Observable<any[]> = this.purchaseFacade.purchaseSteps$;
    activeStep$: Observable<number> = this.purchaseFacade.activePurchaseStep$;

    purchase$ = this.purchaseFacade.purchase$;
    payloads$: Observable<any[]> = this.purchaseFacade.payloads$;
    isFlowCompleted$: Observable<boolean> = this.purchaseFacade.isFlowComplete$;
    // activeStep = 1;
    selectedProduct$ = this.productsFacade.selected$;
    selectedRelatedCard$ = this.relatedCardFacade.selectedCard$;
    selectedMedium$ = this.mediumFacade.medium$;

    // isLoading: boolean;
    // hasProducts: boolean;

    ticketPayloadsPosted$: Observable<boolean> = this.shoppingCartFacade.ticketPayloadsPosted$;
    cart$ = this.shoppingCartFacade.cart$;

    locale = this.translate.currentLang;
    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private productsFacade: ProductsFacade,
        private purchaseFacade: PurchaseFacade,
        private relatedCardFacade: RelatedCardFacade,
        private mediumFacade: MediumFacade,
        private translate: TranslateService,
        private shoppingCartFacade: ShoppingCartFacade
    ) {}

    ngOnInit() {
        this.allIds$.pipe(takeUntil(this.componentDestroyed$)).subscribe((ids) => {
            if (ids.length === 0) {
                this.productsFacade.fetchWalletProducts(this.env.CHANNEL);
            }
        });

        this.purchaseFacade.setWalletPurchaseSteps([
            'TICKET_TEXT_ADD_DATA',
            'TICKET_TEXT_PAYMENT',
            'TICKET_TEXT_TICKETS'
        ]);
        this.purchaseFacade.setRegionId(this.env.REGION_ID);
        this.purchaseFacade.setActiveWalletPurchaseStep(1);
        this.purchaseFacade.setChannel();

        // dont know why type Product is not working here, setting it as any
        this.selectedProduct$.pipe(takeUntil(this.componentDestroyed$)).subscribe((product: any) => {
            if (product) {
                // should check if need to reset medium (incompatible card type mask)
                this.purchaseFacade.setSelectedProduct(product);
                this.mediumFacade.setCardTypeMask(product.card_type_mask ?? 4);
            }
        });

        this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card: any) => {
            if (card) {
                this.mediumFacade.setRelatedCardMedium(card);
            }
        });

        this.selectedMedium$.pipe(takeUntil(this.componentDestroyed$)).subscribe((medium) => {
            if (medium.type) {
                this.purchaseFacade.setWalletSelectedMedium(medium);
            }
        });

        // Set default to UTN, actual card type mask set when product is selected
        this.mediumFacade.setCardTypeMask(4);

        combineLatest([this.payloads$, this.selectedProduct$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([payloads, selectedProduct]) => {
                this.purchaseFacade.setFlowStatus(this.isFlowValid(payloads, selectedProduct));
            });

        this.ticketPayloadsPosted$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isPayloadsPosted) => {
            if (isPayloadsPosted) {
                this.cart$.pipe(take(1)).subscribe((c) => {
                    this.shoppingCartFacade.updateCart(c.shopping_cart_id);
                    this.purchaseFacade.setPurchaseShoppingCartId(c.shopping_cart_id);
                    // scroll window to top and navigate to cart
                    window.scroll(0, 0);
                    this.router.navigate([this.translate.currentLang, 'purchase', 'cart']);
                });
            }
        });
    }

    ngOnDestroy() {
        // Clean up selections
        this.purchaseFacade.clearWalletPurchaseSelections();
        this.productsFacade.setSelectedProduct({ product_id: null });
        this.relatedCardFacade.setSelectedRelatedCard(null);
        this.mediumFacade.clear();

        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    isFlowValid(payloads, selectedProduct) {
        // no payloads, flow incomplete
        if (payloads.length === 0) {
            return false;
        }

        // check if product is present in payloads
        if (payloads.filter((p) => !p.product_id).length > 0) {
            return false;
        }
        // check if medium is present in payloads
        if (
            payloads.filter((p) => {
                return !p.card_id && !p.related_card_id;
            }).length > 0
        ) {
            return false;
        }
        // check if is variable sum product then sum is provided in payloads
        if (
            !selectedProduct ||
            (selectedProduct.product_properties.filter((p) => p.code === 'LOAD_WALLET_MIN').length > 0 &&
                selectedProduct.product_properties.filter((p) => p.code === 'LOAD_WALLET_MAX').length > 0 &&
                payloads.filter((p) => !p.price).length > 0)
        ) {
            return false;
        }
        // check if card tpye matches selected product mask in payloads (not sure if needed currently)
        return true;
    }

    postToCart() {
        // passing in entire flow, as there are required data that needs to be attached to tickets
        this.purchase$.pipe(take(1)).subscribe((flow) => {
            this.shoppingCartFacade.postPayloads(flow);
        });
    }

    /**
     * Converts payloads to local ticket objects.
     *
     * Is required as our ticket component expects certain feilds to be present.
     *
     * @param {*} payload
     * @return {*}
     * @memberof WalletLoadingPageComponent
     */
    convertPayloadsToLocalTickets(payloads) {
        return payloads.map((payload) => {
            const ticket = { ...payload };
            combineLatest([this.selectedRelatedCard$, this.selectedProduct$, this.selectedMedium$])
                .pipe(take(1))
                .subscribe(([relatedCard, selectedProduct, selectedMedium]) => {
                    ticket.product = { ...selectedProduct };
                    if (selectedMedium.type === MediumType.RelatedCard) {
                        ticket.related_card_object = { ...relatedCard };
                    }
                    if (ticket.price) {
                        ticket.product.price = ticket.price;
                    }
                });
            return ticket;
        });
    }
}
